import { LinearSRGBColorSpace, Object3D, WebGLRenderer } from "three";
import Stats from "three/addons/libs/stats.module.js";
import { Binding } from "../Binding/Binding.js";
import { MultiViewsRenderer } from "../MultiviewRenderer/MultiViewRenderer.js";
import { EventsCachedMesh } from "../RaycasterHandler/EventsCachedMesh.js";
import { RaycasterHandler } from "../RaycasterHandler/RaycasterHandler.js";
import { Globals } from "../Utils/Globals.js";
import { SceneBase } from "./SceneBase.js";
class MainBaseInitializer {
    static _init = false;
    static edit3DObjectPrototype() {
        if (!this._init) {
            this._init = true;
            Object3D.prototype.dispose = function () {
                this.needsRender = true;
                EventsCachedMesh.unregisterMeshAndChildren(this);
                Binding.unbindAll(this);
                this.parent?.remove(this);
                //todo add unbind hitbox
            };
        }
    }
}
export class MainBase {
    raycasterHandler;
    multiviewRenderer;
    renderer;
    mouse = { x: 0, y: 0 };
    stats;
    scenes;
    _mouseForRaycaster = { x: 0, y: 0 };
    _touchForRaycaster;
    _showStats = false;
    _fps = 0;
    _dragStartViewport = undefined;
    _smartRendering = false;
    _timerFPS;
    _started;
    //todo add current view port
    onBeforeRaycasting = null;
    onAfterRaycasting = null;
    onBeforeAnimate = null;
    onAfterAnimate = null;
    onBeforeWindowResize = null;
    onAfterWindowResize = null;
    set renderingScene(scene) {
        if (!this.multiViewsActive && this.renderingScene != scene) {
            this.multiviewRenderer.setRenderingScene(scene, scene.camera, scene.composer);
        }
    }
    get renderingScene() {
        return this.multiviewRenderer.renderingScene;
    }
    set showStats(value) {
        this.stats.dom.style.display = value ? "block" : "none";
        this._showStats = value;
    }
    get showStats() {
        return this._showStats;
    }
    set backgroundColor(color) {
        this.multiviewRenderer.backgroundColor = color;
    }
    get backgroundColor() {
        return this.multiviewRenderer.backgroundColor;
    }
    get multiViewsActive() {
        return this.multiviewRenderer.isMultiViewsActive;
    }
    set multiViewsActive(value) {
        this.multiviewRenderer.isMultiViewsActive = value;
    }
    get activeCamera() {
        return this.multiviewRenderer.activeCamera;
    }
    get activeScene() {
        return this.multiviewRenderer.activeScene;
    }
    set smartRendering(value) {
        if (this._smartRendering !== value) {
            if (value) {
                for (let scene of this.scenes) {
                    scene.bindsmartRenderingEvent();
                }
            }
            else {
                for (let scene of this.scenes) {
                    scene.unbindsmartRenderingEvent();
                }
            }
            this._smartRendering = value;
        }
    }
    get smartRendering() {
        return this._smartRendering;
    }
    constructor(canvasContainer, createSceneObj, parameters = {}, rendererParameters) {
        MainBaseInitializer.edit3DObjectPrototype();
        parameters.modelToBind && Binding.init(parameters.modelToBind, parameters.modelToMerge);
        Globals.canvasContainer = canvasContainer;
        this.renderer = new WebGLRenderer(rendererParameters);
        this.renderer.outputColorSpace = LinearSRGBColorSpace;
        this.renderer.setPixelRatio(window.devicePixelRatio);
        this.renderer.setSize(canvasContainer.clientWidth, canvasContainer.clientHeight);
        Globals.canvas = this.renderer.domElement;
        canvasContainer.appendChild(Globals.canvas);
        this.multiviewRenderer = new MultiViewsRenderer(this.renderer, parameters.backgroundColor, parameters.backgroundAlpha);
        this.raycasterHandler = new RaycasterHandler(parameters.enableRaycasterHandler, parameters.enableDragAndDrop, parameters.dragAndDropTriggerButton, parameters.triggerEventsWhileDragging, parameters.cursorEnabled);
        this.scenes = createSceneObj(this.renderer);
        this.renderingScene = this.scenes instanceof Array ? this.scenes[0] : this.scenes;
        this.stats = new Stats();
        canvasContainer.appendChild(this.stats.dom);
        this.showStats = parameters?.showStats ?? false;
        this._fps = parameters?.fps ?? 0;
        this.smartRendering = parameters?.smartRendering ?? false;
        window.addEventListener('resize', () => this.onWindowResize());
        // window.visualViewport?.addEventListener('resize', () => this.onWindowResize()); // to fix iPad panel, TODO use ResizeObserver instead in future
        if (parameters.disableRightClickOnCanvas !== false) {
            Globals.canvas.addEventListener('contextmenu', (event) => {
                event.preventDefault();
            });
        }
        Globals.canvas.addEventListener("mousedown", (event) => {
            event.preventDefault(); //fix edge drag and drop canvas
            this.switchViewport(this._mouseForRaycaster);
        });
        Globals.canvas.addEventListener("touchstart", (event) => {
            if (event.touches.length === 1) {
                this._touchMoveAndStartEvent(event);
                this._touchForRaycaster.firstTouch = true;
                this._touchForRaycaster.isTouch = true;
                this.switchViewport(this._touchForRaycaster);
            }
        });
        Globals.canvas.addEventListener("touchmove", (event) => {
            this._touchMoveAndStartEvent(event);
        });
        document.addEventListener("touchend", (event) => {
            if (event.touches.length === 0 && this._touchForRaycaster) {
                this._touchForRaycaster.lastTouch = true;
            }
        });
        document.addEventListener("touchcancel", (event) => {
            if (event.touches.length === 0 && this._touchForRaycaster) {
                this._touchForRaycaster.lastTouch = true;
            }
        });
        Globals.canvas.addEventListener("mousemove", (event) => {
            this.mouse.x = event.offsetX;
            this.mouse.y = event.offsetY;
            this.multiviewRenderer.getRaycasterCoords(this.mouse, this._mouseForRaycaster);
        });
        window.onblur = () => {
            this.raycasterHandler.triggerEndEvents();
        };
        for (let scene of this.scenes) {
            this.bindSceneEvents(scene);
        }
    }
    bindSceneEvents(scene) {
        this.multiviewRenderer.addEventListener("viewportSwitched", (event) => {
            if (this.raycasterHandler.objectDragging) {
                this.raycasterHandler.dragAndDropViewportCheck = this._dragStartViewport == event.data.newViewport;
            }
        });
        scene.bindEvent("onDragStart", () => {
            this._dragStartViewport = this.multiviewRenderer.activeViewport;
        });
        scene.bindEvent("onDragEnd", () => {
            this._dragStartViewport = undefined;
            this.raycasterHandler.dragAndDropViewportCheck = true;
        });
    }
    switchViewport(target) {
        if (this.multiViewsActive) {
            this.multiviewRenderer.getRaycasterCoordsMultiviews(this.mouse, this.multiviewRenderer.activeViewport, target);
            if (Math.max(Math.abs(target.x), Math.abs(target.y)) > 1) {
                this.multiviewRenderer.updateSelectedView(this.mouse);
            }
        }
    }
    _touchMoveAndStartEvent = (event) => {
        const touch = this.raycasterHandler.getTouch(event.touches);
        if (touch) {
            const rect = event.target.getBoundingClientRect(); //precalcola
            this.mouse.x = touch.clientX - rect.left;
            this.mouse.y = touch.clientY - rect.top;
            this._touchForRaycaster = this.multiviewRenderer.getRaycasterCoords(this.mouse, this._touchForRaycaster);
        }
    };
    updateCanvasMeasures() {
        for (const scene of this.scenes) {
            scene.onWindowResize();
        }
        this.multiviewRenderer.onWindowResize();
        this.renderer.setSize(Globals.canvasContainer.clientWidth, Globals.canvasContainer.clientHeight);
    }
    onWindowResize() {
        this.onBeforeWindowResize && this.onBeforeWindowResize();
        this.updateCanvasMeasures();
        this.onAfterWindowResize && this.onAfterWindowResize();
    }
    addMultiViewElements(...multiViewElements) {
        this.multiviewRenderer.add(...multiViewElements);
    }
    /**
     * Start rendering scenes
     * */
    start() {
        if (!this._started) {
            if (this._fps !== 0) {
                this._timerFPS = setInterval(() => {
                    window.requestAnimationFrame(() => this.animate());
                }, 1000 / this._fps);
            }
            else {
                this.animate(false);
            }
            this._started = true;
        }
    }
    /**
     * Change render frame rate
     * @param value FPS to set. If 0 it will sync with the monitor refresh rate using window.requestAnimationFrame
     */
    changeFPS(value) {
        if (this._fps != value) {
            window.clearInterval(this._timerFPS);
            this._fps = value;
            if (value !== 0) {
                this._timerFPS = setInterval(() => {
                    window.requestAnimationFrame(() => this.animate());
                }, 1000 / this._fps);
            }
            else {
                this.animate(false);
            }
        }
    }
    updateStats() {
        if (this._showStats && this.stats) {
            this.stats.update();
        }
    }
    raycasting() {
        this.onBeforeRaycasting && this.onBeforeRaycasting();
        let coordsToRaycaster = this._touchForRaycaster || this._mouseForRaycaster;
        if (coordsToRaycaster) {
            coordsToRaycaster.firstTouch && this.raycasterHandler.deleteMouseUp();
            this.raycasterHandler.triggerEvents(this.activeScene.meshes, this.activeCamera, coordsToRaycaster);
            delete coordsToRaycaster.firstTouch;
            if (coordsToRaycaster.lastTouch) {
                this._touchForRaycaster = undefined;
                coordsToRaycaster.lastTouch = false;
            }
        }
        this.onAfterRaycasting && this.onAfterRaycasting();
    }
    animate(singleExecution = true) {
        if (this._fps === 0 && !singleExecution) {
            window.requestAnimationFrame(() => this.animate(false));
        }
        EventsCachedMesh.update();
        this.raycasting();
        this.onBeforeAnimate && this.onBeforeAnimate(); //todo put a variabile to animate only selected view
        if (this.scenes instanceof SceneBase) {
            this.scenes.animate();
        }
        else {
            for (const scene of this.scenes) {
                scene.animate();
            }
        }
        this.onAfterAnimate && this.onAfterAnimate();
        this.multiviewRenderer.render() && this.updateStats();
        if (this._smartRendering) {
            for (let scene of this.scenes) {
                scene.needsRender = false;
            }
        }
    }
}
//# sourceMappingURL=MainBase.js.map